import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import interiorCatalogApi from "../services/interiorCatalogApi";
import ItemComponentsEditor from "../components/catalog/ItemComponentsEditor";

const emptyFamily = {
  id: null,
  code: "",
  name: "",
  family_type: "",
  category: "",
  description: "",
  attribute_schema_json: { fields: [] },
  placement_schema_json: {},
  drilling_schema_json: {},
  status: "active",
};

const emptyItem = {
  id: null,
  family_id: "",
  asset_id: "",
  code: "",
  sku: "",
  name: "",
  brand: "",
  manufacturer: "",
  supplier: "",
  finish: "",
  price: "",
  currency: "EUR",
  lead_time_days: "",
  attributes_json: {},
  status: "active",
};

function prettyJson(value) {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return "{}";
  }
}

function parseJsonSafe(value, fallback = {}) {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function searchableJson(value) {
  try {
    return JSON.stringify(value ?? {}).toLowerCase();
  } catch {
    return "";
  }
}

function ToolbarButton({ children, onClick, disabled, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-1.5 rounded border text-sm disabled:opacity-40 ${
        danger
          ? "bg-white text-red-600 border-slate-200 hover:bg-red-50"
          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function TabButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm border-b-2 ${
        active
          ? "border-blue-600 bg-blue-50 text-blue-700 font-medium"
          : "border-transparent text-slate-500 hover:text-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function normalizeList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  return [];
}

const PUBLIC_BASE_URL = "https://modularobjects.com";

function resolveFileUrl(file) {
  const raw = file?.public_url || file?.file_path || "";

  if (!raw) return "";

  const str = String(raw).trim();

  if (str.startsWith("https://modularobjects.com/uploads/")) {
    return encodeURI(
      str.replace(
        "https://modularobjects.com/uploads/",
        "https://modularobjects.com/mailserver/uploads/"
      )
    );
  }

  if (str.startsWith("http://modularobjects.com/uploads/")) {
    return encodeURI(
      str.replace(
        "http://modularobjects.com/uploads/",
        "https://modularobjects.com/mailserver/uploads/"
      )
    );
  }

  if (str.startsWith("http")) return encodeURI(str);

  if (str.startsWith("/mailserver/")) {
    return encodeURI(`${PUBLIC_BASE_URL}${str}`);
  }

  if (str.startsWith("/uploads/")) {
    return encodeURI(`${PUBLIC_BASE_URL}/mailserver${str}`);
  }

  if (str.startsWith("/")) {
    return encodeURI(`${PUBLIC_BASE_URL}${str}`);
  }

  return encodeURI(`${PUBLIC_BASE_URL}/mailserver/${str}`);
}

function formatValue(value, suffix = "") {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.join(", ");
  return `${value}${suffix}`;
}

function ItemPreviewCard({
  item,
  itemFiles,
  productImage,
  productPdf,
  onOpenAssembly,
}) {
  if (!item?.id) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400 text-sm">
        Select item to preview.
      </div>
    );
  }

  const commercial = item.attributes_json?.commercial || {};
  const technical = item.attributes_json?.technical || {};

  return (
    <div className="space-y-4">
      <div className="border rounded bg-white p-4">
        <div className="flex gap-4">
          <div className="w-52 h-40 shrink-0 border rounded bg-slate-50 overflow-hidden flex items-center justify-center">
            {productImage ? (
              <img
                src={resolveFileUrl(productImage)}
                alt={productImage.file_label || item.name}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="text-xs text-slate-400">No image</div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-lg font-semibold text-slate-800 leading-snug">
              {item.name || "Unnamed item"}
            </div>

            <div className="text-sm text-slate-500 mt-2">
              {item.code || "—"} · {item.sku || "—"}
            </div>

            <div className="text-sm text-slate-500 mt-1">
              {item.brand || "—"} · {item.finish || "—"}
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              {productImage ? (
                <a
                  href={resolveFileUrl(productImage)}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 border rounded bg-white text-sm text-blue-700 hover:bg-blue-50"
                >
                  Open image
                </a>
              ) : null}

              {productPdf ? (
                <a
                  href={resolveFileUrl(productPdf)}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 border rounded bg-white text-sm text-blue-700 hover:bg-blue-50"
                >
                  Open product PDF
                </a>
              ) : null}

              <button
                type="button"
                onClick={onOpenAssembly}
                className="px-3 py-1.5 border rounded bg-white text-sm hover:bg-slate-50"
              >
                Assembly
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="border rounded bg-white p-4">
          <div className="font-semibold text-sm text-slate-700 mb-3">
            Commercial
          </div>

          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <div className="text-slate-500">Part number</div>
            <div>{formatValue(commercial.partNumber)}</div>

            <div className="text-slate-500">Item number</div>
            <div>{formatValue(commercial.itemNumber)}</div>

            <div className="text-slate-500">EAN</div>
            <div>{formatValue(commercial.eanCode)}</div>

            <div className="text-slate-500">Pack qty</div>
            <div>{formatValue(commercial.packagingQuantity)}</div>

            <div className="text-slate-500">Weight / 1000 pcs</div>
            <div>{formatValue(commercial.weightPer1000PcsKg, " kg")}</div>
          </div>
        </div>

        <div className="border rounded bg-white p-4">
          <div className="font-semibold text-sm text-slate-700 mb-3">
            Technical
          </div>

          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <div className="text-slate-500">System</div>
            <div>
              {formatValue(
                technical.typeOfRunnerSystem || technical.typeOfLockingDevice
              )}
            </div>

            <div className="text-slate-500">Nominal length</div>
            <div>{formatValue(technical.nominalLengthMm, " mm")}</div>

            <div className="text-slate-500">Drawer length</div>
            <div>{formatValue(technical.drawerLengthMm, " mm")}</div>

            <div className="text-slate-500">Load</div>
            <div>{formatValue(technical.dynamicLoadBearingKg, " kg")}</div>

            <div className="text-slate-500">Min cabinet depth</div>
            <div>{formatValue(technical.minimumInternalCabinetDepthMm, " mm")}</div>

            <div className="text-slate-500">Material</div>
            <div>{formatValue(technical.material)}</div>

            <div className="text-slate-500">Motion</div>
            <div>{formatValue(technical.motionTechnology)}</div>

            <div className="text-slate-500">Packaging</div>
            <div>{formatValue(technical.packaging)}</div>
          </div>
        </div>
      </div>

      <div className="border rounded bg-white p-4">
        <div className="font-semibold text-sm text-slate-700 mb-2">
          Files
        </div>

        {itemFiles?.length ? (
          <div className="space-y-2">
            {itemFiles.map((file) => (
              <a
                key={file.id}
                href={resolveFileUrl(file)}
                target="_blank"
                rel="noreferrer"
                className="block border rounded px-3 py-2 text-sm hover:bg-slate-50"
              >
                <span className="font-medium">
                  {file.file_label || file.original_filename}
                </span>
                <span className="text-slate-400 ml-2">
                  {file.file_role}
                </span>
              </a>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-400">No files uploaded.</div>
        )}
      </div>
    </div>
  );
}

export default function InteriorCatalogPage() {
  const navigate = useNavigate();

  const [families, setFamilies] = useState([]);
  const [items, setItems] = useState([]);
  const [assets, setAssets] = useState([]);

  const [selectedFamilyId, setSelectedFamilyId] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);

  const [familyForm, setFamilyForm] = useState(emptyFamily);
  const [itemForm, setItemForm] = useState(emptyItem);

  const [familyAttributesText, setFamilyAttributesText] = useState(
    prettyJson({ fields: [] })
  );
  const [familyPlacementText, setFamilyPlacementText] = useState(prettyJson({}));
  const [familyDrillingText, setFamilyDrillingText] = useState(prettyJson({}));
  const [itemAttributesText, setItemAttributesText] = useState(prettyJson({}));

  const [loading, setLoading] = useState(true);
  const [savingFamily, setSavingFamily] = useState(false);
  const [savingItem, setSavingItem] = useState(false);
  const [errorText, setErrorText] = useState("");

  const [leftTab, setLeftTab] = useState("families");
  const [rightTab, setRightTab] = useState("general");

  const [familySearch, setFamilySearch] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [itemBrandFilter, setItemBrandFilter] = useState("");
  const [itemStatusFilter, setItemStatusFilter] = useState("");
  const [itemAssetFilter, setItemAssetFilter] = useState("");
  const [assetSearch, setAssetSearch] = useState("");
  const [assetSearching, setAssetSearching] = useState(false);

  const [familyLimit] = useState(50);
const [familyOffset, setFamilyOffset] = useState(0);
const [familyTotal, setFamilyTotal] = useState(0);

const [itemLimit] = useState(50);
const [itemOffset, setItemOffset] = useState(0);
const [itemTotal, setItemTotal] = useState(0);

const [itemComponents, setItemComponents] = useState([]);
const [itemFiles, setItemFiles] = useState([]);


  async function loadFamilies(nextOffset = 0) {
  const res = await interiorCatalogApi.listFamilies({
    search: familySearch,
    limit: familyLimit,
    offset: nextOffset,
  });

  setFamilies(res.items || []);
  setFamilyTotal(res.total || 0);
  setFamilyOffset(nextOffset);
}


async function loadItems(nextOffset = 0) {
  const status =
    itemStatusFilter === "all"
      ? "all"
      : itemStatusFilter || "active";

  const res = await interiorCatalogApi.listFamilyItems({
    search: itemSearch,
    family_id: selectedFamilyId || "",
    brand: itemBrandFilter || "",
    status,
    asset_filter: itemAssetFilter || "",
    limit: itemLimit,
    offset: nextOffset,
  });

  setItems(res.items || []);
  setItemTotal(res.total || 0);
  setItemOffset(nextOffset);
}

async function loadAll() {
  try {
    setLoading(true);
    setErrorText("");

    const [assetsResult, familiesResult, itemsResult] = await Promise.allSettled([
      interiorCatalogApi.listAssets({ limit: 200, offset: 0 }),
      loadFamilies(0),
      loadItems(0),
    ]);

    if (assetsResult.status === "fulfilled") {
      const assetsRes = assetsResult.value;
      setAssets(Array.isArray(assetsRes) ? assetsRes : assetsRes.items || []);
    } else {
      console.error("load assets failed:", assetsResult.reason);
      setAssets([]);
      setErrorText(
        assetsResult.reason?.response?.data?.message ||
          assetsResult.reason?.response?.data?.error ||
          "Assets failed to load, but families/items loaded."
      );
    }
  } catch (err) {
    console.error("loadAll catalog error:", err);
    setErrorText(err?.message || "Failed to load catalog.");
  } finally {
    setLoading(false);
  }
}

async function ensureAssetLoaded(assetId) {
  if (!assetId) return null;

  const exists = assets.find((a) => String(a.id) === String(assetId));
  if (exists) return exists;

  try {
    const res = await interiorCatalogApi.getAssetById(assetId);
    const asset = res?.item || res;

    if (asset?.id) {
      setAssets((prev) => {
        const list = Array.isArray(prev) ? prev : [];

        if (list.some((a) => String(a.id) === String(asset.id))) {
          return list;
        }

        return [asset, ...list];
      });

      return asset;
    }
  } catch (err) {
    console.warn("Could not load selected asset:", assetId, err);
  }

  return null;
}

async function searchAssets(searchText = "") {
  try {
    setAssetSearching(true);

    const res = await interiorCatalogApi.listAssets({
      search: String(searchText || "").trim(),
      limit: 50,
      offset: 0,
    });

    setAssets(normalizeList(res));
  } catch (err) {
    console.error("asset search error:", err);
  } finally {
    setAssetSearching(false);
  }
}

useEffect(() => {
  if (leftTab === "families" && rightTab === "components") {
    setRightTab("general");
  }
}, [leftTab, rightTab]);

useEffect(() => {
  const t = setTimeout(() => {
    loadFamilies(0);
  }, 400);

  return () => clearTimeout(t);
}, [familySearch]);

useEffect(() => {
  const t = setTimeout(() => {
    loadItems(0);
  }, 400);

  return () => clearTimeout(t);
}, [
  itemSearch,
  selectedFamilyId,
  itemBrandFilter,
  itemStatusFilter,
  itemAssetFilter,
]);

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
  if (!assetSearch.trim()) return;

  const t = setTimeout(() => {
    searchAssets(assetSearch);
  }, 350);

  return () => clearTimeout(t);
}, [assetSearch]);

  const selectedFamily = useMemo(
    () =>
      families.find((f) => String(f.id) === String(selectedFamilyId)) || null,
    [families, selectedFamilyId]
  );

  const selectedItem = useMemo(
    () => items.find((i) => String(i.id) === String(selectedItemId)) || null,
    [items, selectedItemId]
  );

  const filteredFamilies = families;

  const filteredItems = items;

  const selectedAsset = useMemo(() => {
  const assetId = itemForm.asset_id;

  if (!assetId) return null;

  return (
    (assets || []).find((asset) => String(asset.id) === String(assetId)) ||
    null
  );
}, [assets, itemForm.asset_id]);

const assetSelectItems = useMemo(() => {
  const list = Array.isArray(assets) ? assets : [];

  if (
    selectedAsset?.id &&
    !list.some((asset) => String(asset.id) === String(selectedAsset.id))
  ) {
    return [selectedAsset, ...list];
  }

  return list;
}, [assets, selectedAsset]);

const selectedItemProductImage = useMemo(() => {
  const files = Array.isArray(itemFiles) ? itemFiles : [];

  return (
    files.find(
      (file) =>
        file.status !== "deleted" &&
        file.file_role === "product_image" &&
        String(file.mime_type || "").startsWith("image/")
    ) ||
    files.find(
      (file) =>
        file.status !== "deleted" &&
        String(file.mime_type || "").startsWith("image/")
    ) ||
    null
  );
}, [itemFiles]);

const selectedItemProductDetailsPdf = useMemo(() => {
  const files = Array.isArray(itemFiles) ? itemFiles : [];

  return (
    files.find(
      (file) =>
        file.status !== "deleted" &&
        file.file_role === "product_details_pdf"
    ) || null
  );
}, [itemFiles]);

  const itemBrandOptions = useMemo(() => {
    return Array.from(
      new Set(
        items.map((item) => String(item.brand || "").trim()).filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [items]);

  function startNewFamily() {
    setLeftTab("families");
    setRightTab("general");
    setSelectedFamilyId(null);
    setFamilyForm({
      ...emptyFamily,
      code: `family_${Date.now()}`,
    });
    setFamilyAttributesText(prettyJson({ fields: [] }));
    setFamilyPlacementText(prettyJson({}));
    setFamilyDrillingText(prettyJson({}));
  }

  function startEditFamily(family) {
    setLeftTab("families");
    setRightTab("general");
    setSelectedFamilyId(family.id);
    setFamilyForm({
      ...emptyFamily,
      ...family,
    });
    setFamilyAttributesText(
      prettyJson(family.attribute_schema_json || { fields: [] })
    );
    setFamilyPlacementText(prettyJson(family.placement_schema_json || {}));
    setFamilyDrillingText(prettyJson(family.drilling_schema_json || {}));
  }

  function startNewItem() {
    setItemComponents([]);
    setItemFiles([]);
    setLeftTab("items");
    setRightTab("general");
    setSelectedItemId(null);
    setItemForm({
      ...emptyItem,
      family_id: selectedFamilyId || "",
      code: `item_${Date.now()}`,
    });
    setItemAttributesText(prettyJson({}));
  }

  async function startEditItem(item) {
  setLeftTab("items");
  setRightTab("general");
  setSelectedItemId(item.id);

  const assetId = item.asset_id || item.linked_asset_id || "";

  try {
  const [componentsRes, filesRes] = await Promise.all([
    interiorCatalogApi.listItemAssets(item.id),
    interiorCatalogApi.listItemFiles(item.id),
  ]);

  setItemComponents(componentsRes.items || []);
  setItemFiles(filesRes.items || []);
} catch (err) {
  console.error("load item components/files error:", err);
  setItemComponents([]);
  setItemFiles([]);
}

  setItemForm({
    ...emptyItem,
    ...item,
    family_id: item.family_id || "",
    asset_id: assetId || "",
    price: item.price ?? "",
    lead_time_days: item.lead_time_days ?? "",
  });

  setItemAttributesText(prettyJson(item.attributes_json || {}));

  if (assetId) {
    await ensureAssetLoaded(assetId);
  }
}

  async function saveFamily() {
    try {
      setSavingFamily(true);

      const payload = {
        ...familyForm,
        attribute_schema_json: parseJsonSafe(familyAttributesText, {
          fields: [],
        }),
        placement_schema_json: parseJsonSafe(familyPlacementText, {}),
        drilling_schema_json: parseJsonSafe(familyDrillingText, {}),
      };

      if (familyForm.id) {
        await interiorCatalogApi.updateFamily(familyForm.id, payload);
      } else {
        await interiorCatalogApi.createFamily(payload);
      }

      await loadAll();
      startNewFamily();
    } catch (err) {
      console.error("saveFamily error:", err);
      alert(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save family."
      );
    } finally {
      setSavingFamily(false);
    }
  }

  async function saveItem() {
    try {
      setSavingItem(true);

      const payload = {
        ...itemForm,
        family_id: Number(itemForm.family_id) || null,
        asset_id: itemForm.asset_id ? Number(itemForm.asset_id) : null,
        price: itemForm.price === "" ? null : Number(itemForm.price),
        lead_time_days:
          itemForm.lead_time_days === ""
            ? null
            : Number(itemForm.lead_time_days),
        attributes_json: parseJsonSafe(itemAttributesText, {}),
      };

      if (itemForm.id) {
        await interiorCatalogApi.updateFamilyItem(itemForm.id, payload);
      } else {
        await interiorCatalogApi.createFamilyItem(payload);
      }

      await loadAll();

if (itemForm.id) {
  const refreshed = await interiorCatalogApi.getFamilyItemById(itemForm.id);
  const nextItem = refreshed?.item || refreshed;

  if (nextItem?.id) {
    await startEditItem(nextItem);
  }
} else {
  startNewItem();
}
    } catch (err) {
      console.error("saveItem error:", err);
      alert(
        err?.response?.data?.message || err?.message || "Failed to save item."
      );
    } finally {
      setSavingItem(false);
    }
  }

  async function deleteFamily() {
    if (!familyForm.id) return;
    if (!window.confirm(`Delete family "${familyForm.name}"?`)) return;

    try {
      await interiorCatalogApi.deleteFamily(familyForm.id);
      await loadAll();
      startNewFamily();
    } catch (err) {
      console.error("deleteFamily error:", err);
      alert(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete family."
      );
    }
  }

  async function deleteItem() {
    if (!itemForm.id) return;
    if (!window.confirm(`Delete item "${itemForm.name}"?`)) return;

    try {
      await interiorCatalogApi.deleteFamilyItem(itemForm.id);
      await loadAll();
      startNewItem();
    } catch (err) {
      console.error("deleteItem error:", err);
      alert(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete item."
      );
    }
  }

  useEffect(() => {
    if (!selectedFamily && !familyForm.id && !familyForm.name && families.length) {
      startNewFamily();
    }
    if (!selectedItem && !itemForm.id && !itemForm.name) {
      startNewItem();
    }
  }, [families.length, items.length]);

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col overflow-hidden bg-slate-100">
      <div className="h-14 shrink-0 border-b bg-white px-3 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-800">
            Interior Catalog
          </div>
          <div className="text-xs text-slate-500">
            Families, items, assets and product attributes
          </div>
        </div>

        <div className="flex gap-2">
          <ToolbarButton onClick={() => navigate("/interior-editor/assets")}>
            Assets
          </ToolbarButton>
          {itemForm.asset_id ? (
  <ToolbarButton
    onClick={() =>
      navigate(`/interior-editor/assets/${itemForm.asset_id}/mounting`)
    }
  >
    Asset Mounting
  </ToolbarButton>
) : null}
          <ToolbarButton onClick={() => navigate("/interior-editor/library")}>
            Library
          </ToolbarButton>
          <ToolbarButton onClick={loadAll}>Refresh</ToolbarButton>
        </div>
      </div>

      {errorText ? (
        <div className="shrink-0 border-b bg-red-50 text-red-700 px-3 py-2 text-sm">
          {errorText}
        </div>
      ) : null}

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-slate-500">
          Loading catalog...
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-[320px_minmax(0,1fr)_430px] overflow-hidden">
          <aside className="min-h-0 border-r bg-white flex flex-col overflow-hidden">
            <div className="h-10 shrink-0 border-b bg-slate-50 flex">
              <button
                type="button"
                onClick={() => setLeftTab("families")}
                className={`flex-1 text-sm ${
                  leftTab === "families"
                    ? "bg-white border-b-2 border-blue-600 text-blue-700"
                    : "text-slate-600"
                }`}
              >
                Families
              </button>

              <button
                type="button"
                onClick={() => setLeftTab("items")}
                className={`flex-1 text-sm ${
                  leftTab === "items"
                    ? "bg-white border-b-2 border-blue-600 text-blue-700"
                    : "text-slate-600"
                }`}
              >
                Items
              </button>
            </div>

            <div className="p-3 border-b bg-white space-y-2">
              {leftTab === "families" ? (
                <>
                  <input
                    value={familySearch}
                    onChange={(e) => setFamilySearch(e.target.value)}
                    placeholder="Search families..."
                    className="w-full px-3 py-2 border rounded text-sm"
                  />

                  <ToolbarButton onClick={startNewFamily}>
                    + New Family
                  </ToolbarButton>
                </>
              ) : (
                <>
                  <input
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                    placeholder="Search items..."
                    className="w-full px-3 py-2 border rounded text-sm"
                  />

                  <select
                    value={itemBrandFilter}
                    onChange={(e) => setItemBrandFilter(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                  >
                    <option value="">All brands</option>
                    {itemBrandOptions.map((brand) => (
                      <option key={brand} value={brand}>
                        {brand}
                      </option>
                    ))}
                  </select>

                  <select
                    value={itemStatusFilter}
                    onChange={(e) => setItemStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                  >
                    <option value="">Active only</option>
<option value="all">All statuses</option>
<option value="active">active</option>
<option value="draft">draft</option>
<option value="inactive">inactive</option>
                  </select>

                  <select
                    value={itemAssetFilter}
                    onChange={(e) => setItemAssetFilter(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                  >
                    <option value="">All asset states</option>
                    <option value="with_asset">With asset</option>
                    <option value="without_asset">Without asset</option>
                  </select>

                  <ToolbarButton onClick={startNewItem}>
                    + New Item
                  </ToolbarButton>
                </>
              )}
            </div>

            <div className="flex-1 min-h-0 overflow-auto p-2 space-y-2">
              {leftTab === "families" ? (
                filteredFamilies.length ? (
                  filteredFamilies.map((family) => (
                    <button
                      key={family.id}
                      type="button"
                      onClick={() => startEditFamily(family)}
                      className={`w-full text-left border rounded p-3 ${
                        selectedFamilyId === family.id
                          ? "bg-blue-50 border-blue-300"
                          : "bg-white hover:bg-slate-50"
                      }`}
                    >
                      <div className="font-medium text-sm">
                        {family.name || "Unnamed family"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {family.code || "—"}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        {family.family_type || "—"} · {family.category || "—"}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="text-sm text-slate-400 p-3">
                    No families found.
                  </div>
                )
              ) : filteredItems.length ? (
                filteredItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => startEditItem(item)}
                    className={`w-full text-left border rounded p-3 ${
                      selectedItemId === item.id
                        ? "bg-blue-50 border-blue-300"
                        : "bg-white hover:bg-slate-50"
                    }`}
                  >
                    <div className="font-medium text-sm">
                      {item.name || "Unnamed item"}
                    </div>
                    <div className="text-xs text-slate-500">
                      {item.code || "—"} · {item.sku || "—"}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {item.brand || "—"} · {item.finish || "—"}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1">
  {Number(item.component_count || 0) > 0
    ? `${item.component_count} components`
    : item.asset_id
    ? "single asset"
    : "no asset"}
</div>

{Number(item.linked_family_count || 0) > 1 ? (
  <div className="text-[11px] text-blue-500 mt-1">
    {item.linked_family_count} families
  </div>
) : null}
                  </button>
                ))
              ) : (
                <div className="text-sm text-slate-400 p-3">
                  No items found.
                </div>
              )}
            </div>
          </aside>

          <main className="min-h-0 border-r bg-white flex flex-col overflow-hidden">
            <div className="h-10 shrink-0 border-b bg-slate-50 px-3 flex items-center justify-between">
              <div className="font-semibold text-sm text-slate-700">
                {leftTab === "families" ? "Family Overview" : "Item Overview"}
              </div>
              <div className="text-xs text-slate-500">
                {leftTab === "families"
                  ? `${filteredFamilies.length} families`
                  : `${filteredItems.length} items`}
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-auto p-4">
              {leftTab === "families" ? (
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 border-b">Name</th>
                        <th className="text-left p-2 border-b">Code</th>
                        <th className="text-left p-2 border-b">Type</th>
                        <th className="text-left p-2 border-b">Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFamilies.map((family) => (
                        <tr
                          key={family.id}
                          onClick={() => startEditFamily(family)}
                          className={`cursor-pointer border-b hover:bg-slate-50 ${
                            selectedFamilyId === family.id ? "bg-blue-50" : ""
                          }`}
                        >
                          <td className="p-2 font-medium">{family.name}</td>
                          <td className="p-2">{family.code}</td>
                          <td className="p-2">{family.family_type || "—"}</td>
                          <td className="p-2">{family.category || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
  <ItemPreviewCard
    item={itemForm}
    itemFiles={itemFiles}
    productImage={selectedItemProductImage}
    productPdf={selectedItemProductDetailsPdf}
    onOpenAssembly={() =>
      itemForm.id &&
      navigate(`/interior-editor/catalog/items/${itemForm.id}/assembly`)
    }
  />
)}
            </div>
          </main>

          <aside className="min-h-0 bg-white flex flex-col overflow-hidden">
            <div className="h-10 shrink-0 border-b bg-slate-50 flex">
  {(leftTab === "items"
  ? ["general", "components", "schema", "json"]
  : ["general", "schema", "json"]
).map((tab) => (
  <TabButton
    key={tab}
    active={rightTab === tab}
    onClick={() => setRightTab(tab)}
  >
    {tab}
  </TabButton>
))}
</div>

            <div className="flex-1 min-h-0 overflow-auto p-4">
              {leftTab === "families" ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="font-semibold text-slate-800">
                        {familyForm.id ? "Edit Family" : "New Family"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {familyForm.code || "No code"}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {familyForm.id ? (
                        <ToolbarButton onClick={deleteFamily} danger>
                          Delete
                        </ToolbarButton>
                      ) : null}
                      <ToolbarButton
                        onClick={saveFamily}
                        disabled={savingFamily}
                      >
                        {savingFamily ? "Saving..." : "Save"}
                      </ToolbarButton>
                    </div>
                  </div>

                  {rightTab === "general" && (
                    <div className="space-y-3">
                      <Field label="Code">
                        <input
                          value={familyForm.code}
                          onChange={(e) =>
                            setFamilyForm((p) => ({
                              ...p,
                              code: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border rounded"
                        />
                      </Field>

                      <Field label="Name">
                        <input
                          value={familyForm.name}
                          onChange={(e) =>
                            setFamilyForm((p) => ({
                              ...p,
                              name: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border rounded"
                        />
                      </Field>

                      <Field label="Family Type">
                        <input
                          value={familyForm.family_type}
                          onChange={(e) =>
                            setFamilyForm((p) => ({
                              ...p,
                              family_type: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border rounded"
                        />
                      </Field>

                      <Field label="Category">
                        <input
                          value={familyForm.category}
                          onChange={(e) =>
                            setFamilyForm((p) => ({
                              ...p,
                              category: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border rounded"
                        />
                      </Field>

                      <Field label="Description">
                        <textarea
                          value={familyForm.description}
                          onChange={(e) =>
                            setFamilyForm((p) => ({
                              ...p,
                              description: e.target.value,
                            }))
                          }
                          rows={5}
                          className="w-full px-3 py-2 border rounded"
                        />
                      </Field>
                    </div>
                  )}

                  {rightTab === "schema" && (
                    <div className="space-y-3">
                      <Field label="Attribute Schema JSON">
                        <textarea
                          value={familyAttributesText}
                          onChange={(e) =>
                            setFamilyAttributesText(e.target.value)
                          }
                          rows={10}
                          className="w-full px-3 py-2 border rounded font-mono text-xs"
                        />
                      </Field>

                      <Field label="Placement Schema JSON">
                        <textarea
                          value={familyPlacementText}
                          onChange={(e) =>
                            setFamilyPlacementText(e.target.value)
                          }
                          rows={8}
                          className="w-full px-3 py-2 border rounded font-mono text-xs"
                        />
                      </Field>

                      <Field label="Drilling Schema JSON">
                        <textarea
                          value={familyDrillingText}
                          onChange={(e) =>
                            setFamilyDrillingText(e.target.value)
                          }
                          rows={8}
                          className="w-full px-3 py-2 border rounded font-mono text-xs"
                        />
                      </Field>
                    </div>
                  )}

                  {rightTab === "json" && (
                    <pre className="text-xs bg-slate-50 border rounded p-3 overflow-auto">
                      {JSON.stringify(familyForm, null, 2)}
                    </pre>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="font-semibold text-slate-800">
                        {itemForm.id ? "Edit Item" : "New Item"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {itemForm.code || "No code"}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {itemForm.id ? (
                        <>
                          <ToolbarButton
                            onClick={() =>
                              navigate(
                                `/interior-editor/catalog/items/${itemForm.id}/mounting`
                              )
                            }
                          >
                            Mounting
                          </ToolbarButton>
                          <ToolbarButton
  onClick={() =>
    navigate(`/interior-editor/catalog/items/${itemForm.id}/assembly`)
  }
>
  Assembly
</ToolbarButton>
                          <ToolbarButton onClick={deleteItem} danger>
                            Delete
                          </ToolbarButton>
                        </>
                      ) : null}

                      <ToolbarButton onClick={saveItem} disabled={savingItem}>
                        {savingItem ? "Saving..." : "Save"}
                      </ToolbarButton>
                    </div>
                  </div>

                  {rightTab === "general" && (
                    <div className="space-y-3">

                      <Field label="Family">
                        <select
                          value={itemForm.family_id}
                          onChange={(e) =>
                            setItemForm((p) => ({
                              ...p,
                              family_id: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border rounded"
                        >
                          <option value="">Select family</option>
                          {families.map((family) => (
                            <option key={family.id} value={family.id}>
                              {family.name} ({family.code})
                            </option>
                          ))}
                        </select>
                      </Field>

                      <Field label="Asset Search">
  <input
    value={assetSearch}
    onChange={(e) => setAssetSearch(e.target.value)}
    placeholder="Search asset by name, code, filename..."
    className="w-full px-3 py-2 border rounded"
  />

  {assetSearching && (
    <div className="mt-1 text-xs text-slate-500">
      Searching assets...
    </div>
  )}
</Field>

                      <Field label="Asset">
  <select
    value={itemForm.asset_id || ""}
    onChange={(e) =>
      setItemForm((p) => ({
        ...p,
        asset_id: e.target.value,
      }))
    }
    className="w-full px-3 py-2 border rounded bg-white"
  >
    <option value="">No asset</option>

    {assetSelectItems.map((asset) => (
      <option key={asset.id} value={asset.id}>
        {asset.name || asset.code || asset.filename || `Asset ${asset.id}`} ·
        id {asset.id} · {asset.conversion_status || "—"}
      </option>
    ))}
  </select>
</Field>

                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Code">
                          <input
                            value={itemForm.code}
                            onChange={(e) =>
                              setItemForm((p) => ({
                                ...p,
                                code: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border rounded"
                          />
                        </Field>

                        <Field label="SKU">
                          <input
                            value={itemForm.sku}
                            onChange={(e) =>
                              setItemForm((p) => ({
                                ...p,
                                sku: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border rounded"
                          />
                        </Field>
                      </div>

                      <Field label="Name">
                        <input
                          value={itemForm.name}
                          onChange={(e) =>
                            setItemForm((p) => ({
                              ...p,
                              name: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border rounded"
                        />
                      </Field>

                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Brand">
                          <input
                            value={itemForm.brand}
                            onChange={(e) =>
                              setItemForm((p) => ({
                                ...p,
                                brand: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border rounded"
                          />
                        </Field>

                        <Field label="Manufacturer">
                          <input
                            value={itemForm.manufacturer}
                            onChange={(e) =>
                              setItemForm((p) => ({
                                ...p,
                                manufacturer: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border rounded"
                          />
                        </Field>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Supplier">
                          <input
                            value={itemForm.supplier}
                            onChange={(e) =>
                              setItemForm((p) => ({
                                ...p,
                                supplier: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border rounded"
                          />
                        </Field>

                        <Field label="Finish">
                          <input
                            value={itemForm.finish}
                            onChange={(e) =>
                              setItemForm((p) => ({
                                ...p,
                                finish: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border rounded"
                          />
                        </Field>
                      </div>

                      <div className="text-[11px] text-slate-400 mt-1">
  {Number(itemForm.component_count || 0) > 0
    ? `${itemForm.component_count} components`
    : itemForm.asset_id
    ? "single asset"
    : "no asset"}
</div>

{Number(itemForm.linked_family_count || 0) > 1 ? (
  <div className="text-[11px] text-blue-500 mt-1">
    {itemForm.linked_family_count} families
    {itemForm.linked_family_names ? ` · ${itemForm.linked_family_names}` : ""}
  </div>
) : null}

                      <div className="grid grid-cols-3 gap-3">
                        <Field label="Price">
                          <input
                            type="number"
                            value={itemForm.price}
                            onChange={(e) =>
                              setItemForm((p) => ({
                                ...p,
                                price: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border rounded"
                          />
                        </Field>

                        <Field label="Currency">
                          <input
                            value={itemForm.currency}
                            onChange={(e) =>
                              setItemForm((p) => ({
                                ...p,
                                currency: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border rounded"
                          />
                        </Field>

                        <Field label="Lead Time">
                          <input
                            type="number"
                            value={itemForm.lead_time_days}
                            onChange={(e) =>
                              setItemForm((p) => ({
                                ...p,
                                lead_time_days: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border rounded"
                          />
                        </Field>
                      </div>
                    </div>
                  )}

                  {rightTab === "components" && (
  <ItemComponentsEditor
    item={itemForm}
    components={itemComponents}
    setComponents={setItemComponents}
    assets={assetSelectItems}
    assetSearch={assetSearch}
    setAssetSearch={setAssetSearch}
  />
)}

                  {rightTab === "schema" && (
                    <Field label="Attributes JSON">
                      <textarea
                        value={itemAttributesText}
                        onChange={(e) => setItemAttributesText(e.target.value)}
                        rows={18}
                        className="w-full px-3 py-2 border rounded font-mono text-xs"
                      />
                    </Field>
                  )}

                  {rightTab === "json" && (
                    <pre className="text-xs bg-slate-50 border rounded p-3 overflow-auto">
                      {JSON.stringify(itemForm, null, 2)}
                    </pre>
                  )}
                </>
              )}
            </div>
          </aside>
        </div>
      )}

      <div className="h-6 shrink-0 border-t bg-white px-3 flex items-center justify-between text-[11px] text-slate-500">
        <div>
          Families: {families.length} · Items: {items.length} · Assets:{" "}
          {assets.length}
        </div>
        <div>
          Selected:{" "}
          {leftTab === "families"
            ? familyForm.name || "none"
            : itemForm.name || "none"}
        </div>
      </div>
    </div>
  );
}